import { Box, Grid } from "@mui/material";
import { Provider } from "@/services";
import { ReactNode, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Flex from "../../../../../components/Flex";
import { ScrollArea } from "../../../../../components/ScrollBar";
import ProviderProfileDrawer from "../../../../../components/app/ProviderProfileDrawer";
import ProviderUserModal from "../../../../../components/app/admin/provider-groups/ProviderUserDrawer";
import CustomCard from "../../../../../components/app/settings/user-settings/CustomCard";
import UserProfileDialog from "../../../../../components/app/settings/user-settings/UserProfileDrawer";
import { STATE_LIST } from "../../../../../constants";
import { ProviderUserType } from "../../../../../constants/ProviderUserSchema";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useProvidersList } from "../../../../../queries/provider/setting/use-providers-list";
import { useUpdateProviderUserStatus } from "../../../../../queries/use-update-provider-status";
import { useUpdateProviderUser } from "../../../../../queries/use-update-provider-user";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_UUID_NAME_LIST,
  DEFAULT_VALUE_LABEL_LIST,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
import { CurView } from "../../../admin/tenant-settings/tenant-providers";
import ProviderUserTable from "../user-setting/ProviderUserTable";
import { GridPaginationModel, GridSortItem } from "@mui/x-data-grid";
import Text from "../../../../../components/Text";
import CustomTablePagination from "../../../../../components/CustomTablePagination";
import { useProvider } from "../../../../../queries/use-provider";
import { UserCardData } from "../../../../../types/user-types";

const ProviderUserDetails = () => {
  const { curView, searchKey, profileData, statusKey, isArchieved }: any =
    useOutletContext();
  const { mutateAsync: updateProviderUser } = useUpdateProviderUser();

  const [
    userProfileModalDisclosure,
    providerProfileModal,
    providerUserModalDisclosure,
    statusDisclosure,
  ] = useReduceDisclosure(4);

  const [selectedUserData, setSelectedUserData] = useState<UserCardData | null>(
    null
  );

  const [selectedProviderUserId, setSelectedProviderUserId] =
    useState<string>();
  const { data: providerDetail } = useProvider({
    providerUuid: selectedProviderUserId,
    tenantId: getXtenantId(),
  });

  // Pagination state for CustomTablePagination
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const { data: ProvidersList, refetch } = useProvidersList({
    pagination: paginationModel,
    searchKey,
    status: statusKey,
    gridSorting,
    archive: isArchieved,
  });
  const { mutateAsync: providerStatusUpdate } = useUpdateProviderUserStatus();
  const showSnackbar = useSnackbar();

  // Get pagination data directly from API response
  const totalElements = ProvidersList?.data?.page?.totalElements ?? 0;

  // const indexOfLastCard: number =
  //   pagination.currentPage * pagination.recordsPerPage; //index of last card to be positioned per page
  // const usersData = UserData.slice(indexOfFirstCard, indexOfLastCard); //array of cards to be displayed on current page
  // const indexOfFirstCard: number = indexOfLastCard - pagination.recordsPerPage; //index position of first card on that page

  function editProviderUser(values: ProviderUserType) {
    // The form holds display shapes the generated model cannot describe —
    // multi-select option lists, a string npi — which the transform above and
    // the API convert back on submit.
    const transformedData = getUnNecessaryTransformData(
      ["acceptedInsurances", "licensedStates", "workLocations", "specialities"],
      values
    );
    return updateProviderUser({
      providerData: transformedData as unknown as Provider,
      xTENANTID: getXtenantId(),
    });
  }

  function handleOpenEditModal(userData: UserCardData) {
    setSelectedProviderUserId(userData?.uuid);
    setSelectedUserData(userData);
    // Todo need to remove while check actual role where dummy role check added

    // to open the provider user form modal
    providerUserModalDisclosure.open();
  }

  function handleStatusChange(userId: string, status: boolean) {
    return providerStatusUpdate({
      providerId: userId,
      status,
      xTENANTID: getXtenantId(),
    })
      .then((res) => {
        refetch();
        statusDisclosure.close();
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  const ACTIVE_VIEW_MAPPER: Record<CurView, ReactNode> = {
    card_view: (
      <Grid container gap={1} justifyContent="center">
        {ProvidersList?.data?.content?.map((user) => {
          return (
            <Grid item key={user.value}>
              <CustomCard
                userData={user}
                handleOpenEditModal={() => handleOpenEditModal(user)}
                handleOpenProfileModal={() => {
                  setSelectedUserData(user);

                  providerProfileModal.open();
                }}
                onStatusChange={() =>
                  handleStatusChange(user.uuid, !user.active)
                }
              />
            </Grid>
          );
        })}
      </Grid>
    ),
    table_view: (
      <ProviderUserTable
        userList={ProvidersList?.data?.content ?? []}
        searchKey={searchKey}
        gridSorting={gridSorting}
        setGridSorting={setGridSorting}
        refetch={refetch}
      />
    ),
  };
  {
    /* <Grid container gap={1} justifyContent="center">
          {usersData.map((user) => {
            return (
              <Grid item key={user.value}>
                <CustomCard
                  userData={user}
                  handleOpenEditModal={() => handleOpenEditModal(user)}
                  handleOpenProfileModal={() => {
                    user.role === "billing_staff"
                      ? handleOpenProfileModal(user)
                      : providerProfileModal.open();
                  }}
                  onStatusChange={() =>
                    handleStatusChange(user.id, user.active)
                  }
                />
              </Grid>
            );
          })}
        </Grid> */
  }
  return (
    <Box>
      <ScrollArea marginTop={"75px"} height="calc(100vh - 235px)">
        {ACTIVE_VIEW_MAPPER[curView as CurView]}
      </ScrollArea>
      <Flex
        justifyContent={{
          sm: "center",
          md: "flex-end",
        }}
        position={"fixed"}
        bottom={"50px"}
        right={"18px"}
        width={"96%"}
        bgcolor={"white"}
        zIndex={"1000"}
        padding={"0px 5px 5px 0px"}
        alignItems={"baseline"}
      >
        <Flex alignItems={"center"} gap={"4px"}>
          <Text color={"text.primary"}>Current page:</Text>
          <Text color={"text.primary"}>{paginationModel.page + 1}</Text>
          <CustomTablePagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalElements}
          />
        </Flex>
      </Flex>
      <ProviderProfileDrawer
        disClosureHost={providerProfileModal}
        providerUuid={selectedUserData?.uuid}
        profileData={profileData}
      />
      <UserProfileDialog
        disClosureHost={userProfileModalDisclosure}
        userId={selectedUserData?.uuid}
        dialogTitle="User Profile"
      />
      <ProviderUserModal
        shouldGetPrefillList
        prefillSpecialtyList={transformMultiSelectFrontEndVal(
          DEFAULT_UUID_NAME_LIST,
          profileData?.specialities
        )?.map((it) => ({ uuid: it?.uuid, name: it?.name }))}
        open={providerUserModalDisclosure.opened}
        title={"Edit Provider User"}
        onClose={() => {
          providerUserModalDisclosure.close();
          setSelectedProviderUserId(undefined);
        }}
        onSubmit={editProviderUser}
        prefillValues={
          {
            ...providerDetail?.data,
            providerType: providerDetail?.data?.providerType ?? undefined,
            // groupNpiNumber: groupNpi,
            acceptedInsurances:
              providerDetail?.data?.acceptedInsurances &&
              transformMultiSelectFrontEndVal(
                DEFAULT_VALUE_LABEL_LIST,
                providerDetail?.data?.acceptedInsurances
              ),
            ageGroupSeen: transformMultiSelectFrontEndVal(
              DEFAULT_VALUE_LABEL_LIST,
              providerDetail?.data?.ageGroupSeen
            ),
            careService: transformMultiSelectFrontEndVal(
              DEFAULT_VALUE_LABEL_LIST,
              providerDetail?.data?.careService
            ),
            workLocations:
              providerDetail?.data?.workLocations &&
              transformMultiSelectFrontEndVal(
                DEFAULT_VALUE_LABEL_LIST,
                providerDetail?.data?.workLocations
              ),
            licensedStates:
              providerDetail?.data?.licensedStates &&
              transformMultiSelectFrontEndVal(
                STATE_LIST,
                providerDetail?.data?.licensedStates
              ),
            spokenLanguages: transformMultiSelectFrontEndVal(
              DEFAULT_UUID_NAME_LIST,
              providerDetail?.data?.spokenLanguages
            ),
            specialities:
              providerDetail?.data?.specialities &&
              transformMultiSelectFrontEndVal(
                DEFAULT_UUID_NAME_LIST,
                providerDetail?.data?.specialities
              ),
          } as ProviderUserType
        }
      />
    </Box>
  );
};
export default ProviderUserDetails;
